import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  SelectChangeEvent,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { BASE_URL } from '../config';

interface Skill {
  id: number;
  nome: string;
  categoria: string | null;
  enabled: boolean;
}

interface RisorsaSkill {
  id: number;
  nome: string;
  categoria: string | null;
  livello: string;
  risorsa_skill_id: number;
}

interface ResourceSkillsManagerProps {
  idRisorsa: number | undefined;
}

const LIVELLI = ['Base', 'Intermedio', 'Avanzato', 'Expert'];

const ResourceSkillsManager: React.FC<ResourceSkillsManagerProps> = ({ idRisorsa }) => {
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
  const [risorsaSkills, setRisorsaSkills] = useState<RisorsaSkill[]>([]);
  const [selectedSkillId, setSelectedSkillId] = useState<number | ''>('');
  const [selectedLivello, setSelectedLivello] = useState<string>('Intermedio');

  useEffect(() => {
    loadAvailableSkills();
    if (idRisorsa) {
      loadRisorsaSkills();
    }
  }, [idRisorsa]);

  const loadAvailableSkills = async () => {
    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/skills`, { headers });
      if (response.ok) {
        const data = await response.json();
        setAvailableSkills(Array.isArray(data) ? data.filter((s: Skill) => s.enabled) : []);
      }
    } catch (error) {
      console.error('Error loading available skills:', error);
    }
  };

  const loadRisorsaSkills = async () => {
    if (!idRisorsa) return;

    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/skills/risorsa/${idRisorsa}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setRisorsaSkills(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading risorsa skills:', error);
    }
  };

  const handleAddSkill = async () => {
    if (!idRisorsa || !selectedSkillId) {
      alert('Seleziona una skill da aggiungere');
      return;
    }

    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/skills/risorsa/${idRisorsa}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          id_skill: selectedSkillId,
          livello: selectedLivello,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore nell\'aggiunta della skill');
      }

      loadRisorsaSkills();
      setSelectedSkillId('');
      setSelectedLivello('Intermedio');
    } catch (error: any) {
      alert(error.message || 'Errore nell\'aggiunta della skill');
    }
  };

  const handleRemoveSkill = async (idSkill: number) => {
    if (!idRisorsa) return;
    if (!window.confirm('Rimuovere questa skill dalla risorsa?')) return;

    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/skills/risorsa/${idRisorsa}/${idSkill}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore nella rimozione della skill');
      }

      loadRisorsaSkills();
    } catch (error: any) {
      alert(error.message || 'Errore nella rimozione della skill');
    }
  };

  const handleChangeLivello = async (idSkill: number, newLivello: string) => {
    if (!idRisorsa) return;

    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/skills/risorsa/${idRisorsa}/${idSkill}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ livello: newLivello }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore nell\'aggiornamento del livello');
      }

      loadRisorsaSkills();
    } catch (error: any) {
      alert(error.message || 'Errore nell\'aggiornamento del livello');
    }
  };

  if (!idRisorsa) {
    return (
      <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Salva prima la risorsa per gestire le skills
        </Typography>
      </Box>
    );
  }

  const skillsNotAssigned = availableSkills.filter(
    s => !risorsaSkills.some(rs => rs.id === s.id)
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Skills / Competenze
      </Typography>

      {/* Form per aggiungere skill */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Seleziona Skill</InputLabel>
          <Select
            value={selectedSkillId}
            label="Seleziona Skill"
            onChange={(e: SelectChangeEvent<number | ''>) => setSelectedSkillId(e.target.value as number | '')}
          >
            <MenuItem value="">
              <em>Nessuna</em>
            </MenuItem>
            {skillsNotAssigned.map((skill) => (
              <MenuItem key={skill.id} value={skill.id}>
                {skill.nome} {skill.categoria ? `(${skill.categoria})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Livello</InputLabel>
          <Select
            value={selectedLivello}
            label="Livello"
            onChange={(e) => setSelectedLivello(e.target.value)}
          >
            {LIVELLI.map((livello) => (
              <MenuItem key={livello} value={livello}>
                {livello}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddSkill}
          disabled={!selectedSkillId}
        >
          Aggiungi
        </Button>
      </Box>

      {/* Tabella skills assegnate */}
      {risorsaSkills.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Skill</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Categoria</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Livello</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 80 }}>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {risorsaSkills.map((skill) => (
                <TableRow key={skill.id} hover>
                  <TableCell>{skill.nome}</TableCell>
                  <TableCell>
                    {skill.categoria ? (
                      <Chip label={skill.categoria} size="small" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={skill.livello}
                      onChange={(e) => handleChangeLivello(skill.id, e.target.value)}
                      sx={{ minWidth: 120 }}
                    >
                      {LIVELLI.map((livello) => (
                        <MenuItem key={livello} value={livello}>
                          {livello}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveSkill(skill.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Nessuna skill assegnata a questa risorsa
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ResourceSkillsManager;
